
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';

interface StatusUpdateDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRequest: any;
  newStatus: string;
  setNewStatus: (status: string) => void;
  isProcessing: boolean;
  updateRequestStatus: () => void;
}

const StatusUpdateDialog: React.FC<StatusUpdateDialogProps> = ({
  isOpen,
  onOpenChange,
  selectedRequest,
  newStatus,
  setNewStatus,
  isProcessing,
  updateRequestStatus
}) => {
  if (!selectedRequest) return null;
  
  // Format status for display - consistently map between DB and UI terms
  const formatStatus = (status: string) => {
    if (status === 'pending_verification') return 'pending';
    if (status === 'verified') return 'approved';
    return status;
  };
  
  // Get current status
  const currentStatus = selectedRequest?.status || 'unknown';
  const displayStatus = formatStatus(currentStatus);
  
  const handleUpdate = () => {
    updateRequestStatus();
    // Dialog will be closed after successful update
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Request Status</DialogTitle>
          <DialogDescription>
            {selectedRequest && (
              <div className="mt-2">
                <p><strong>Company:</strong> {selectedRequest.company_name}</p>
                <p><strong>Contact:</strong> {selectedRequest.full_name}</p>
                <p><strong>Email:</strong> {selectedRequest.email}</p>
                <p className="flex items-center">
                  <strong>Current Status:</strong>{' '}
                  <span className={`ml-2 px-2 py-1 rounded text-sm ${
                    displayStatus === 'approved' ? 'bg-green-100 text-green-800' : 
                    displayStatus === 'rejected' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {displayStatus}
                  </span>
                </p>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
          
        <div className="my-6">
          <label className="block text-sm font-medium mb-2">New Status</label>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Select new status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending Verification</SelectItem>
              <SelectItem value="approved">Approved (Grant Access)</SelectItem>
              <SelectItem value="rejected">Rejected (Deny Access)</SelectItem>
            </SelectContent>
          </Select>
            
          {newStatus === 'approved' && (
            <div className="mt-4 p-3 bg-blue-50 text-blue-800 rounded-md text-sm">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Approving this request will:</p>
                  <ul className="list-disc ml-5 mt-1">
                    <li>Change the user's status to approved</li>
                    <li>Send an automated email with the NinjaOne dashboard link</li>
                    <li>Allow the user to access the dashboard</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {newStatus === 'rejected' && (
            <div className="mt-4 p-3 bg-amber-50 text-amber-800 rounded-md text-sm">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Rejecting this request will:</p>
                  <ul className="list-disc ml-5 mt-1">
                    <li>Change the user's status to rejected</li>
                    <li>Send an automated email notification to the user</li>
                    <li>User will not be able to access the dashboard</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
          
        <DialogFooter className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button variant="outline" disabled={isProcessing}>Cancel</Button>
          </DialogClose>
          <Button 
            disabled={!newStatus || isProcessing}
            onClick={handleUpdate}
            variant={newStatus === 'approved' ? 'default' : newStatus === 'rejected' ? 'destructive' : 'outline'}
          >
            {isProcessing ? (
              <span className="flex items-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </span>
            ) : (
              `Update to ${newStatus || ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StatusUpdateDialog;
